import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import SafeImage from '../../../components/SafeImage';

const MAINTENANCE_MSG = 'Sorry, this game is under maintenance. Please try another game.';

/**
 * Game-library picker shown in the "Add Games" dialog.
 *
 * Flow:
 *   1. Player taps a game row → that row becomes the *selected* one
 *      (single-select / radio behaviour). No request is fired yet.
 *   2. Player taps the bottom "Create account" button.
 *   3. The selected row's tick circle animates through `creating →
 *      success / error`, the per-game status persists, and the
 *      selection clears so the player can immediately pick another
 *      game and create a second account in the same session
 *      (up to 10 in a row, one at a time).
 *   4. On any provider failure we surface a friendly maintenance
 *      message instead of the raw error string.
 */
export default function AddGamePanel({ games, accounts, close, gameCreations, onStartCreate }) {
  const [selectedId, setSelectedId] = useState(null);

  // gameCreations is owned by the parent (PlayerApp) so an in-flight
  // creation survives the dialog being closed and re-opened. Shape:
  //   { [gameId]: { status: 'creating' | 'success' | 'error', game, error?, account? } }
  const statuses = useMemo(() => {
    const out = {};
    Object.entries(gameCreations || {}).forEach(([gid, v]) => { out[gid] = v.status; });
    return out;
  }, [gameCreations]);
  const errors = useMemo(() => {
    const out = {};
    Object.entries(gameCreations || {}).forEach(([gid, v]) => { if (v.error) out[gid] = v.error; });
    return out;
  }, [gameCreations]);

  const ownedKeys = useMemo(() => {
    const s = new Set();
    accounts.forEach(a => { if (a.game_id) s.add(a.game_id); if (a.game_code) s.add(a.game_code); });
    return s;
  }, [accounts]);

  const selectedGame = useMemo(
    () => games.find(g => g.id === selectedId) || null,
    [games, selectedId]
  );

  const isCreating = selectedGame ? statuses[selectedGame.id] === 'creating' : false;

  const startCreate = async () => {
    if (!selectedGame) {
      toast.error('Pick a game first');
      return;
    }
    if (statuses[selectedGame.id] === 'creating' || statuses[selectedGame.id] === 'success') return;

    const game = selectedGame;
    // Hand off to PlayerApp so the request keeps running even after the
    // dialog closes. This also lets the player start a *second* game
    // creation in parallel (just pick a different row and Create again).
    if (onStartCreate) {
      try { onStartCreate(game); } catch { /* noop */ }
    }
    if (close) {
      close();
    }
    // Clear selection immediately so the user can pick another game and
    // create it without waiting for the first one to finish.
    setSelectedId(null);
  };

  const ACCENT = '#3B82F6';

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-4">
      <div className="flex-1 min-h-0 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
        {games.length === 0 ? (
          <p className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>No games available right now.</p>
        ) : (
          <div className="space-y-3 py-2 mx-auto" style={{ maxWidth: '320px' }}>
            {games.map(g => {
              const isOwned = ownedKeys.has(g.id) || ownedKeys.has(g.code);
              const status = statuses[g.id];
              const isRowCreating = status === 'creating';
              const isSuccess = status === 'success';
              const isError = status === 'error';
              const isSelected = selectedId === g.id;
              const isActive = isSelected || isSuccess || isRowCreating; // visually highlighted row
              const rawImg = g.logo_image_url || g.logo_url || g.image_url || g.image || null;
              const img = rawImg
                ? (rawImg.startsWith('http') ? rawImg : `${process.env.REACT_APP_BACKEND_URL || ''}${rawImg}`)
                : 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=300&fit=crop';

              // Tick-circle visual states
              let circleBg = 'transparent';
              let circleBorder = '2px solid #FFFFFF';
              let circleShadow = 'none';
              if (isSuccess) {
                circleBg = '#22FF88';
                circleBorder = 'none';
                circleShadow = '0 0 0 5px rgba(34,255,136,0.30), 0 0 14px rgba(34,255,136,0.55)';
              } else if (isError) {
                circleBg = '#EF4444';
                circleBorder = 'none';
                circleShadow = '0 0 0 5px rgba(239,68,68,0.30), 0 0 14px rgba(239,68,68,0.55)';
              } else if (isRowCreating) {
                circleBg = '#000000';
                circleBorder = 'none';
                circleShadow = '0 0 0 5px rgba(255,255,255,0.20)';
              } else if (isSelected) {
                circleBg = '#000000';
                circleBorder = 'none';
                circleShadow = '0 0 0 5px rgba(255,255,255,0.35)';
              }

              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    if (isOwned || isRowCreating || isSuccess) return;
                    // Single-select radio toggle. Tapping the same row a
                    // second time deselects it.
                    setSelectedId(prev => (prev === g.id ? null : g.id));
                  }}
                  disabled={isOwned || isRowCreating || isSuccess}
                  className="w-full flex items-center gap-4 text-left transition rounded-2xl"
                  style={{
                    opacity: isOwned ? 0.4 : 1,
                    cursor: isOwned ? 'not-allowed' : (isRowCreating ? 'wait' : 'pointer'),
                    padding: isActive ? '8px 14px' : '8px 0',
                    background: isActive ? '#FFFFFF' : 'transparent',
                    boxShadow: isActive ? '0 8px 22px rgba(255,255,255,0.18)' : 'none'
                  }}
                  data-testid={`game-row-${g.id}`}
                  aria-pressed={isSelected || isSuccess}
                  aria-busy={isRowCreating}
                >
                  <span
                    className="shrink-0 flex items-center justify-center transition relative rounded-full"
                    style={{
                      width: '28px',
                      height: '28px',
                      background: circleBg,
                      border: circleBorder,
                      boxShadow: circleShadow
                    }}
                    aria-hidden
                  >
                    {isRowCreating && (
                      <span
                        className="absolute inset-[-3px] rounded-full"
                        style={{
                          background:
                            'conic-gradient(from 0deg, rgba(34,255,136,0) 0deg, #22FF88 90deg, rgba(34,255,136,0) 180deg, rgba(34,255,136,0) 360deg)',
                          animation: 'spin 0.9s linear infinite',
                          filter: 'drop-shadow(0 0 6px rgba(34,255,136,0.55))',
                        }}
                      />
                    )}
                    {isSuccess && <Check className="w-4 h-4 relative" style={{ color: '#02140A' }} strokeWidth={4} />}
                    {isError && <X className="w-4 h-4 relative" style={{ color: '#FFFFFF' }} strokeWidth={4} />}
                    {isSelected && !isRowCreating && !isSuccess && !isError && (
                      <Check className="w-4 h-4 relative" style={{ color: '#FFFFFF' }} strokeWidth={4} />
                    )}
                  </span>

                  <div
                    className="rounded-xl overflow-hidden shrink-0"
                    style={{
                      width: '78px',
                      height: '64px',
                      border: isActive ? '2px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.08)'
                    }}
                  >
                    <SafeImage
                      src={img}
                      alt={g.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      fallback={<div className="w-full h-full flex items-center justify-center bg-black text-xl font-black text-white">{g.name?.charAt(0) || '?'}</div>}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p
                      className="display font-bold truncate"
                      style={{
                        color: isActive ? '#000000' : '#FFFFFF',
                        fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
                        fontSize: '17px',
                        lineHeight: 1.2
                      }}
                    >
                      {g.name}
                    </p>
                    {isOwned && !status && (
                      <p className="text-[11px] uppercase tracking-wider mt-1" style={{ color: 'rgba(134,239,172,0.85)' }}>Already added</p>
                    )}
                    {isSuccess && (
                      <p className="text-[11px] uppercase tracking-wider mt-1 font-bold" style={{ color: '#16A34A' }}>Account created</p>
                    )}
                    {isError && (
                      <p className="text-[11px] mt-1 font-semibold" style={{ color: '#EF4444' }}>
                        {errors[g.id] || MAINTENANCE_MSG}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer — Create account (primary) and Done (secondary). Done is
          always enabled now so closing the dialog while a creation is
          running is fine — the request keeps running in the background. */}
      <div className="pt-4 grid grid-cols-2 gap-3 shrink-0">
        <Button
          onClick={close}
          variant="outline"
          className="h-12 display font-bold rounded-full"
          style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#FFFFFF', background: 'transparent' }}
          data-testid="add-game-done"
        >
          Done
        </Button>
        <Button
          onClick={startCreate}
          disabled={!selectedGame || isCreating}
          className="h-12 display font-extrabold rounded-full disabled:opacity-50"
          style={{
            background: ACCENT,
            color: '#FFFFFF',
            boxShadow: selectedGame ? `0 0 0 1px ${ACCENT}, 0 8px 22px ${ACCENT}66` : 'none'
          }}
          data-testid="add-game-create"
        >
          {isCreating ? (
            <span className="flex items-center gap-2 justify-center">
              <div className="spinner w-4 h-4" />
              Creating…
            </span>
          ) : 'Create account'}
        </Button>
      </div>
    </div>
  );
}
